import React, { useEffect, useState } from "react";
import { ImagePlus, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { ImageCropUpload } from "@/components/ImageCropUpload";
import { defaultAvatar } from "@/lib/defaults";

export const AvatarNudgeCard = ({ onClose }) => {
  const { user, t, refreshMe } = useApp();
  const [busy, setBusy] = useState(false);

  // Stamp « seen » on display, then refresh the local profile so the card never comes back (any screen, any device).
  useEffect(() => { api.post("/users/me/avatar-nudge/seen").then(() => refreshMe()).catch(() => {}); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const uploaded = async (url) => {
    setBusy(true);
    try {
      await api.patch("/users/me", { avatar_url: url });
      await refreshMe();
      toast.success(t("avatar_nudge.saved", "Photo de profil ajoutée ✓"));
      onClose();
    } catch (e) {
      toast.error(e?.friendlyMessage || e?.message || "Erreur");
    } finally { setBusy(false); }
  };

  return (
    <div className="yalola-card p-4 relative animate-in fade-in slide-in-from-top-2 duration-500" data-testid="avatar-nudge-card">
      <button type="button" onClick={onClose} className="absolute top-3 right-3 p-1 rounded-full hover:opacity-70" aria-label={t("avatar_nudge.later", "Plus tard")} data-testid="avatar-nudge-close">
        <X size={16} style={{ color: "var(--yalola-text-muted)" }} />
      </button>
      <div className="flex items-start gap-3 pr-6">
        <Avatar className="h-14 w-14 shrink-0 ring-2 ring-offset-2" style={{ "--tw-ring-color": "var(--yalola-primary-soft)" }} data-testid="avatar-nudge-preview">
          <AvatarImage src={defaultAvatar(user?.gender)} alt="" />
          <AvatarFallback>{user?.name?.[0]}</AvatarFallback>
        </Avatar>
        <div className="min-w-0">
          <div className="font-bold font-display" data-testid="avatar-nudge-title">{t("avatar_nudge.title", "Ajoute une photo de profil")}</div>
          <p className="text-sm mt-0.5" style={{ color: "var(--yalola-text-muted)" }} data-testid="avatar-nudge-desc">
            {t("avatar_nudge.desc", "Un visage rend ton profil plus chaleureux et aide les autres membres à te reconnaître. Tu pourras la changer à tout moment depuis ton profil.")}
          </p>
        </div>
      </div>
      <div className="flex items-center justify-end gap-2 mt-3">
        <Button variant="ghost" size="sm" className="rounded-full" onClick={onClose} disabled={busy} data-testid="avatar-nudge-later">{t("avatar_nudge.later", "Plus tard")}</Button>
        <ImageCropUpload
          aspect={1}
          cropShape="round"
          icon={ImagePlus}
          buttonLabel={t("avatar_nudge.choose", "Choisir une photo")}
          title={t("profile.adjust_avatar", "Ajuster la photo de profil")}
          onUploaded={uploaded}
          disabled={busy}
          primary
          testId="avatar-nudge-upload"
        />
      </div>
    </div>
  );
};

export default AvatarNudgeCard;
